import { createContext, useContext, type ReactNode } from 'react';
import { useWorkflowConnectors } from '@/hooks/useWorkflowConnectors';
import type { NodeConnectorInfo } from '@/types/connections';

type ConnectorContextValue = {
  getConnectorInfo: (connectionName: string) => NodeConnectorInfo | undefined;
  isLoading: boolean;
};

const ConnectorContext = createContext<ConnectorContextValue | undefined>(undefined);

type ConnectorProviderProps = {
  children: ReactNode;
};

/**
 * Provider that makes connector info available to all workflow nodes
 */
export const ConnectorProvider = ({ children }: ConnectorProviderProps) => {
  const { getConnectorInfo, isLoading } = useWorkflowConnectors();

  return (
    <ConnectorContext.Provider value={{ getConnectorInfo, isLoading }}>
      {children}
    </ConnectorContext.Provider>
  );
};

/**
 * Hook to get connector info for a specific connection
 */
export const useConnectorInfo = (connectionName: string | undefined): NodeConnectorInfo | undefined => {
  const context = useContext(ConnectorContext);

  if (!connectionName) return undefined;
  if (!context) return undefined;

  return context.getConnectorInfo(connectionName);
};

/**
 * Hook to access the full connector context
 */
export const useConnectorContext = (): ConnectorContextValue => {
  const context = useContext(ConnectorContext);

  if (context === undefined) {
    throw new Error('useConnectorContext must be used within a ConnectorProvider');
  }

  return context;
};

/**
 * Hook to get the getConnectorInfo function safely (returns no-op outside provider)
 */
export const useGetConnectorInfo = (): ((connectionName: string) => NodeConnectorInfo | undefined) => {
  const context = useContext(ConnectorContext);
  return context?.getConnectorInfo ?? (() => undefined);
};
